// Puerto de packages/hce/src/hce/paciente/NotasTab.tsx.
import { useMedplum } from '@medplum/react-hooks';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { InsigniaFormularioPropio } from '@/components/InsigniaFormularioPropio';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { notify } from '@/components/ui/sonner';
import { SelectorFormularios } from './SelectorFormularios';
import { asegurarEncounterDeHoy } from './asegurarEncounterDeHoy';
import { TablaVacia } from './TablaVacia';
import { useTitulosFormulario } from './useTitulosFormulario';

function useRespuestasConRefresh(patientId: string) {
  const medplum = useMedplum();
  const [version, setVersion] = useState(0);
  const [respuestas, setRespuestas] = useState<QuestionnaireResponse[] | undefined>(undefined);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    medplum
      .searchResources('QuestionnaireResponse', { patient: `Patient/${patientId}`, _sort: '-authored', _count: 200 })
      .then((r) => {
        if (!cancelado) {
          setRespuestas(r as QuestionnaireResponse[]);
          setCargando(false);
        }
      })
      .catch(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientId, version]);

  return [respuestas, cargando, () => setVersion((v) => v + 1)] as const;
}

export function NotasTab(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [respuestas, cargando, refrescar] = useRespuestasConRefresh(id);
  const [abierto, setAbierto] = useState(false);
  const [encounterRef, setEncounterRef] = useState<string>();
  const [preparando, setPreparando] = useState(false);
  const [descartando, setDescartando] = useState<string | null>(null);

  async function descartar(qr: QuestionnaireResponse): Promise<void> {
    if (!qr.id) return;
    setDescartando(qr.id);
    try {
      // "stopped" (no "entered-in-error"): el valor FHIR pensado exactamente
      // para esto — un borrador abandonado a medio camino, sin que se sepa
      // (ni importe) si se retomará. Nunca se borra el recurso: mismo
      // criterio de "todo evento queda, nada se edita en silencio" que el
      // resto del proyecto (ver revocatoria de consentimientos).
      await medplum.updateResource<QuestionnaireResponse>({ ...qr, status: 'stopped' });
      refrescar();
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescartando(null);
    }
  }

  const alternar = async (): Promise<void> => {
    if (!abierto && !encounterRef) {
      setPreparando(true);
      try {
        setEncounterRef(await asegurarEncounterDeHoy(medplum, id));
      } catch (err) {
        notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
        setPreparando(false);
        return;
      }
      setPreparando(false);
    }
    setAbierto((v) => !v);
  };

  const lista = (respuestas ?? []) as QuestionnaireResponse[];
  const titulos = useTitulosFormulario(lista);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.notas')}</h5>
        <div className="flex items-center gap-3">
          <Button size="sm" variant={abierto ? 'secondary' : 'default'} onClick={() => void alternar()} disabled={preparando}>
            {abierto ? t('fichaPaciente.notas.cerrarNuevaNota') : t('fichaPaciente.notas.nuevaNota')}
          </Button>
        </div>
      </div>
      <Collapsible open={abierto}>
        <CollapsibleContent>
          {/* Pedido explícito de Sue 2026-09-27 (auditoría de visibilidad por
              especialidad): "Notas" vuelve a mostrar el catálogo COMPLETO
              (Tramo A + todos los grupos del Tramo B con algún código
              permitido), no solo Tramo A — cada grupo SIGUE teniendo también
              su propia pestaña dedicada (Episodio quirúrgico, UCI, etc.),
              esto es una duplicación deliberada para que nada quede "fuera
              de la vista" de Notas, no un reemplazo de esas pestañas. */}
          <SelectorFormularios subject={`Patient/${id}`} encounter={encounterRef} />
        </CollapsibleContent>
      </Collapsible>

      {cargando ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : lista.length === 0 ? (
        <TablaVacia mensaje={t('fichaPaciente.sinDatos')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.formulario')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.estado')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.map((qr) => {
              const info = qr.questionnaire ? titulos.get(qr.questionnaire) : undefined;
              const qs = new URLSearchParams({ respuestaId: qr.id ?? '' });
              if (qr.encounter?.reference) qs.set('encounter', qr.encounter.reference);
              return (
                <TableRow key={qr.id}>
                  <TableCell>{qr.authored ? new Date(qr.authored).toLocaleString('es-EC') : '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      {info?.editable ? (
                        <Link className="text-hce-pine underline-offset-2 hover:underline" to={`/${id}/formulario/${info.codigo}?${qs.toString()}`}>
                          {t(`hub.formulario.${info.codigo}`, info.titulo)}
                        </Link>
                      ) : (
                        (info?.titulo ?? qr.questionnaire ?? '—')
                      )}
                      {info && <InsigniaFormularioPropio codigo={info.codigo} />}
                    </div>
                  </TableCell>
                  <TableCell>{t(`fichaPaciente.estadoFormulario.${qr.status}`, qr.status)}</TableCell>
                  <TableCell>
                    {qr.status === 'in-progress' && (
                      <Button size="sm" variant="ghost" disabled={descartando === qr.id} onClick={() => void descartar(qr)}>
                        {descartando === qr.id ? t('fichaPaciente.notas.descartando') : t('fichaPaciente.notas.descartar')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
